/* eslint-disable @typescript-eslint/no-explicit-any */
import { FloatingFormItem, Group } from '@/components/_common';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Empty,
  Form,
  FormInstance,
  Input,
  Row,
  Space,
  Typography,
} from 'antd';
import React, { useEffect, useState } from 'react';
import AddressForm from '@/components/_common/AddressModal/AddressForm';
import { AddressDefaultType } from '@/components/_common/AddressModal/types';
import { useInvoiceCFEFormContext } from '../InvoiceCFEFormProvider';
import {
  TaxCouponDetailRecipientFormValues,
  TaxCouponStatus,
} from '@/core/domain/coupon/coupons/coupons.types';
import { applyMask } from '@/utils/mask';
import { cepMask } from '@/utils/mask/commonMasks';
import { FaCheck } from 'react-icons/fa';
import { useSupplierMutation } from '@/core/domain/people/Suppliers/Suppliers.hooks';
import { useInsurerMutation } from '@/core/domain/people/Insurers/Insurers.hooks';
import SelectRecipientType from '../../InvoiceForms/InvoiceRecipientForm/RecipientForm/SelectRecipientType';
import { InvoiceRecipientType } from '@/core/domain/invoice/invoices/invoices.enums';
import { InvoiceNFeRecipientFormValues } from '@/core/domain/invoice/invoice/Invoice.types';
import { useCustomerAddressesMutation } from '@/core/domain/people/CustomerAddresses/CustomerAddresses.hooks';

interface Props {
  form: FormInstance<TaxCouponDetailRecipientFormValues>;
  isEditRecipient: boolean;
}

const InvoiceCFeRecipientForm: React.FC<Props> = ({
  form,
  isEditRecipient,
}) => {
  const formValues = Form.useWatch([], form);

  const [formAddress] = Form.useForm<AddressDefaultType>();
  const formAddressValue = Form.useWatch([], formAddress);

  const { taxCoupon } = useInvoiceCFEFormContext();

  const disabled = taxCoupon?.status !== TaxCouponStatus.TYPING;

  const [manualAddress, setManualAddress] = useState<boolean>(false);

  const [recipientAddresses, setRecipientAddresses] = useState<
    AddressDefaultType[]
  >([]);

  const [addressId, setAddressId] = useState<number>();
  const [hasAddress, setHasAddress] = useState(false);

  const customerAddress = useCustomerAddressesMutation();
  const supplierDetail = useSupplierMutation();
  const insurerDetail = useInsurerMutation();

  useEffect(() => {
    if (formAddressValue && isEditRecipient) {
      form.setFieldsValue({
        deliveryAddress: {
          street: formAddressValue.street,
          streetNumber: formAddressValue.number,
          complement: formAddressValue.complement,
          district: formAddressValue.district,
          city: String(formAddressValue.city),
          cityId: formAddressValue.cityId,
          state: String(formAddressValue.state),
          zipcode: formAddressValue.zipCode,
        },
      });
    }
  }, [formAddressValue, isEditRecipient]);

  function setRecipientData(recipient: InvoiceNFeRecipientFormValues) {
    if (recipient.recipientType === InvoiceRecipientType.CUSTOMER) {
      form.setFieldsValue({
        id: recipient.customerId,
        recipientName: recipient.customer,
        cpfCnpj: recipient.cpfCnpj,
      });
    } else if (recipient.recipientType === InvoiceRecipientType.INSURER) {
      form.setFieldsValue({
        id: recipient.insurerId,
        recipientName: recipient.insurer,
        cpfCnpj: recipient.cpfCnpj,
      });
    } else if (recipient.recipientType === InvoiceRecipientType.SUPPLIER) {
      form.setFieldsValue({
        id: recipient.supplierId,
        recipientName: recipient.supplier,
        cpfCnpj: recipient.cpfCnpj,
      });
    }
  }

  useEffect(() => {
    (async () => {
      const recipient = formValues?.recipient;

      if (
        recipient?.recipientType === InvoiceRecipientType.CUSTOMER &&
        recipient?.customerId
      ) {
        const addresses = await customerAddress.mutateAsync(
          recipient?.customerId,
        );
        setRecipientAddresses(addresses.data || []);
        setRecipientData(recipient);
      }
      if (
        recipient?.recipientType === InvoiceRecipientType.SUPPLIER &&
        recipient?.supplierId
      ) {
        const result = await supplierDetail.mutateAsync(recipient?.supplierId);
        setRecipientAddresses(result?.address ? [result.address] : []);
        setRecipientData(recipient);
      }
      if (
        recipient?.recipientType === InvoiceRecipientType.INSURER &&
        recipient?.insurerId
      ) {
        const result = await insurerDetail.mutateAsync(recipient?.insurerId);
        setRecipientAddresses(result?.addressesDTO || []);
        setRecipientData(recipient);
      }
    })();
  }, [formValues?.recipient]);

  useEffect(() => {
    if (taxCoupon?.recipient?.deliveryAddress) {
      formAddress.setFieldsValue({
        ...taxCoupon.recipient?.deliveryAddress,
        zipCode: taxCoupon.recipient?.deliveryAddress.zipcode,
        number: taxCoupon.recipient?.deliveryAddress.streetNumber,
      });
    }
  }, [taxCoupon]);

  useEffect(() => {
    if (!isEditRecipient) {
      setManualAddress(false);
      setHasAddress(false);
      setRecipientAddresses([]);
      setAddressId(undefined);
    }
  }, [isEditRecipient]);

  function applyAddress(address?: AddressDefaultType) {
    if (address) {
      form.setFieldValue('deliveryAddress', {
        zipcode: address.zipCode,
        district: address.district,
        state: address.state || '',
        street: address.street,
        streetNumber: address.number,
        city: address.city || '',
        cityId: address.cityId,
        complement: address.complement,
      });
    } else {
      form.setFieldValue(['deliveryAddress'], undefined);
    }
  }

  return (
    <>
      <Form form={form} name="recipient">
        <Row gutter={[16, 16]}>
          <Col span={24} hidden={isEditRecipient}>
            <Group position="between" style={{ marginBottom: 24 }}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                Dados do destinatário
              </Typography.Title>
            </Group>

            <Form.Item hidden name="id" />

            <Row gutter={[16, 16]}>
              <Col span={24} md={12}>
                <FloatingFormItem name="recipientName" label="Destinatário">
                  <Input disabled />
                </FloatingFormItem>
              </Col>

              <Col span={24} md={12}>
                <FloatingFormItem name="cpfCnpj" label="CPF/CNPJ">
                  <Input disabled />
                </FloatingFormItem>
              </Col>
            </Row>
          </Col>

          <Col span={24} hidden={!isEditRecipient}>
            <Group position="between" style={{ marginBottom: 24 }}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                Alterar destinatário
              </Typography.Title>
            </Group>

            <SelectRecipientType form={form as any} />
          </Col>

          {isEditRecipient && (
            <Col span={24}>
              <Row>
                <Col span={24}>
                  <Checkbox
                    disabled={disabled || !formValues.recipient?.cpfCnpj}
                    onChange={({ target }) => setHasAddress(target.checked)}
                    checked={hasAddress}
                  >
                    Informar endereço do destinatário
                  </Checkbox>
                </Col>

                {hasAddress && (
                  <Col span={24}>
                    <div>
                      {!manualAddress && (
                        <>
                          <Group
                            position="between"
                            style={{ marginBottom: 16 }}
                          >
                            <Typography.Title style={{ margin: 0 }} level={5}>
                              Endereços do destinatário
                            </Typography.Title>

                            <Button
                              type="link"
                              onClick={() => setManualAddress(true)}
                            >
                              Novo endereço
                            </Button>
                          </Group>

                          {!recipientAddresses.length && (
                            <Card style={{ borderRadius: 8 }}>
                              <Empty description="Não há endereço cadastrado para este destinatário, informe um novo endereço" />
                            </Card>
                          )}

                          {recipientAddresses.map((address) => {
                            const addressComplement = address.complement
                              ? `, ${address.complement}`
                              : '';
                            const addressCity = address.city
                              ? `- ${address.city}`
                              : '';
                            const addressState = address.state
                              ? `- ${address.state}`
                              : '';

                            return (
                              <Card
                                style={{
                                  marginBottom: 16,
                                  borderRadius: 8,
                                  borderColor:
                                    addressId === address.id
                                      ? '#364068'
                                      : undefined,
                                }}
                                bodyStyle={{ padding: 16 }}
                              >
                                <Group>
                                  <Space
                                    direction="vertical"
                                    style={{ width: '100%' }}
                                    size={0}
                                  >
                                    <strong>
                                      {applyMask(address.zipCode, cepMask)}
                                    </strong>
                                    <p
                                      style={{ margin: 0 }}
                                    >{`${address.street}, ${address.number} ${addressComplement}`}</p>
                                    <p
                                      style={{ margin: 0 }}
                                    >{`${address.district} ${addressCity} ${addressState}`}</p>
                                  </Space>

                                  <Button
                                    icon={<FaCheck size={12} />}
                                    size="large"
                                    type="primary"
                                    ghost={addressId !== address.id}
                                    onClick={() => {
                                      if (addressId !== address.id) {
                                        setAddressId(address.id);
                                        applyAddress(address);
                                      } else {
                                        applyAddress(undefined);
                                        setAddressId(undefined);
                                      }
                                    }}
                                  />
                                </Group>
                              </Card>
                            );
                          })}
                        </>
                      )}

                      {manualAddress && (
                        <>
                          <Group
                            position="between"
                            style={{ marginBottom: 16 }}
                          >
                            <Typography.Title style={{ margin: 0 }} level={5}>
                              Informar um novo endereço
                            </Typography.Title>
                            <Button
                              type="link"
                              onClick={() => setManualAddress(false)}
                            >
                              Selecionar endereço
                            </Button>
                          </Group>

                          <AddressForm
                            form={formAddress}
                            simpleForm
                            isRequired
                          />
                        </>
                      )}
                    </div>
                  </Col>
                )}
              </Row>
            </Col>
          )}

          <Col
            hidden={
              isEditRecipient || !taxCoupon?.recipient?.deliveryAddress?.zipcode
            }
          >
            <Typography.Title level={4}>Endereço</Typography.Title>
            <Row gutter={[16, 16]}>
              <Col span={24} md={12} lg={6}>
                <FloatingFormItem
                  label="CEP"
                  name={['deliveryAddress', 'zipcode']}
                >
                  <Input disabled />
                </FloatingFormItem>
              </Col>

              <Col span={24} md={12} lg={6}>
                <FloatingFormItem
                  label="Logradouro"
                  name={['deliveryAddress', 'street']}
                >
                  <Input disabled />
                </FloatingFormItem>
              </Col>

              <Col span={24} md={12} lg={6}>
                <FloatingFormItem
                  label="Número"
                  name={['deliveryAddress', 'streetNumber']}
                >
                  <Input disabled />
                </FloatingFormItem>
              </Col>

              <Col span={24} md={12} lg={6}>
                <FloatingFormItem
                  label="Complemento"
                  name={['deliveryAddress', 'complement']}
                >
                  <Input disabled />
                </FloatingFormItem>
              </Col>

              <Col span={24} md={12} lg={6}>
                <FloatingFormItem
                  label="Bairro"
                  name={['deliveryAddress', 'district']}
                >
                  <Input disabled />
                </FloatingFormItem>
              </Col>

              <Col span={24} md={12} lg={6}>
                <FloatingFormItem
                  label="Cidade"
                  name={['deliveryAddress', 'city']}
                >
                  <Input disabled />
                </FloatingFormItem>
              </Col>

              <Col span={12} hidden>
                <FloatingFormItem hidden name={['deliveryAddress', 'cityId']}>
                  <Input disabled />
                </FloatingFormItem>
              </Col>

              <Col span={24} md={12} lg={6}>
                <FloatingFormItem
                  label="Estado"
                  name={['deliveryAddress', 'state']}
                >
                  <Input disabled />
                </FloatingFormItem>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>
    </>
  );
};

export default InvoiceCFeRecipientForm;
